import { useState, useCallback, useMemo } from 'react';

import { map } from 'lodash';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import PageLayout from 'src/components/PageLayout';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';

import { ImageGallery } from 'src/components/ImageGallery';
import LoaderIndicator from 'src/components/LoaderIndicator';
import { useGetAssetsPictures } from 'src/pages/ProjectDetailsPage/api';
import { useGetCandidateDetails } from 'src/pages/CandidatesDetailsPage/api';
import { formatNumber, formatPhoneNumber } from 'src/utils/standards';
import { locale } from 'src/config';

import messages from './messages';
import CandidateShiftsTab, {
  CandidateTabProps,
} from './components/CandidateShiftsTab';
import CandidateLeavesTab from './components/CandidateLeavesTab';
import CandidateFinanceTab from './components/CandidateFinanceTab';
import CandidateBehaviourTab from './components/CandidateBehaviourTab';
import CandidateJobSettingsTab from './components/CandidateJobSettingsTab';
import CandidateAppSettingsTab from './components/CandidateAppSettingsTab';
import CandidatePersonalInfoTab from './components/CandidatePersonalInfoTab';
import CandidateQualificationsTab from './components/CandidateQualificationsTab';
import CandidateAdditionalFieldsTab from './components/CandidateAdditionalFieldsTab';

const noIndicator = { style: { display: 'none' } };

const tabs: Array<{
  value: string;
  label: string;
  component?: React.FC<CandidateTabProps>;
}> = [
  // {
  //   value: 'summary',
  //   label: 'Summary',
  //   component: CandidateSummaryTab,
  // },
  {
    value: 'shifts',
    label: 'Shifts',
    component: CandidateShiftsTab,
  },
  {
    value: 'leaves',
    label: 'Leaves',
    component: CandidateLeavesTab,
  },
  {
    value: 'behaviour',
    label: 'Behaviour',
    component: CandidateBehaviourTab,
  },
  // {
  //   value: 'contract',
  //   label: 'Contract',
  //   component: CandidateContractTab,
  // },
  {
    value: 'personal_info',
    label: 'Personal Info',
    component: CandidatePersonalInfoTab,
  },
  {
    value: 'finance',
    label: 'Finance',
    component: CandidateFinanceTab,
  },
  {
    value: 'job_settings',
    label: 'Job Settings',
    component: CandidateJobSettingsTab,
  },
  {
    value: 'app_settings',
    label: 'App Settings',
    component: CandidateAppSettingsTab,
  },
  {
    value: 'qualifications',
    label: 'Qualifications',
    component: CandidateQualificationsTab,
  },
  {
    value: 'additional_fields',
    label: 'Additional Fields',
    component: CandidateAdditionalFieldsTab,
  },
];

const paperStylesSx = {
  width: '100%',
  borderRadius: 2,
};

const tabStylesSx = {
  px: 3,
  py: 2,
};

export default function CandidatesDetailsPage() {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const { uuid } = useParams<{ uuid: string }>();

  const [activeTab, setActiveTab] = useState(0);

  const { data: candidate, isLoading: isCandidateLoading } =
    useGetCandidateDetails({
      uuid: uuid,
      enabled: Boolean(uuid),
    });

  const { data: pictures } = useGetAssetsPictures({
    uuid: candidate?.data?.id,
    enabled: Boolean(candidate?.data?.id),
  });

  const candidateName = useMemo(() => {
    if (!candidate) return '';
    const { data } = candidate;
    return `${data?.firstName ?? ''} ${data?.lastName ?? ''}`;
  }, [candidate?.data]);

  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue);
    },
    [],
  );

  const CurrentTab = useMemo(() => {
    if (tabs?.[activeTab]) {
      return {
        id: candidate?.data?.id,
        uuid: candidate?.data?.uuid,
        component: tabs?.[activeTab].component,
      };
    }
    return null;
  }, [candidate?.data?.id, candidate?.data?.uuid, activeTab]);

  return (
    <PageLayout backHref="/students">
      {isCandidateLoading ? (
        <Grid
          container
          width="100%"
          position="relative"
          justifyContent="center"
        >
          <LoaderIndicator />
        </Grid>
      ) : (
        <>
          <Grid container>
            <Paper sx={paperStylesSx}>
              <Grid
                size={12}
                container
                spacing={8}
                padding={2}
                alignItems="center"
              >
                <Grid container spacing={2} alignItems="center">
                  <ImageGallery pictures={pictures} height={92} width={92} />

                  <Grid container spacing={1} flexDirection="column">
                    <Typography variant="h4">{candidateName}</Typography>
                    <Grid container spacing={1} alignItems="center">
                      <Typography variant="h5">
                        {candidate?.data?.id}
                      </Typography>
                      <Box
                        width={5}
                        height={5}
                        borderRadius={10}
                        bgcolor={theme.palette.grey[500]}
                      />
                      <Typography variant="h5">
                        {candidate?.data?.status?.name}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid container spacing={1} flexDirection="column">
                  <Grid container alignItems="center">
                    <LocalPhoneOutlinedIcon fontSize="small" />
                    {map(candidate?.data?.phoneNumbers, (phoneNumber) => (
                      <Typography key={phoneNumber.id} variant="h5">
                        {formatPhoneNumber(
                          phoneNumber?.callingCode.toString(),
                          phoneNumber.number,
                        )}
                      </Typography>
                    ))}
                  </Grid>

                  <Grid container alignItems="center">
                    <EmailOutlinedIcon fontSize="small" />
                    <Typography variant="h5">
                      {candidate?.data?.email}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={1} flexDirection="column">
                  {/* <Typography variant="h5">
                    {formatMessage(messages.ageLabel, {
                      age: candidate?.data?.age ?? '-',
                    })}
                  </Typography> */}
                  <Typography variant="h5">
                    {formatMessage(messages.ratingLabel, {
                      score:
                        formatNumber(
                          candidate?.data?.averageJobPerformanceRating,
                          locale,
                        ) ?? '-',
                    })}
                  </Typography>
                </Grid>

                {!!candidate?.data?.certificates?.length && (
                  <Grid container spacing={1} flexDirection="column">
                    <Typography variant="h5">
                      {formatMessage(messages.certificatesLabel)}
                    </Typography>
                    <Grid container spacing={1}>
                      {map(candidate?.data?.certificates, (certificate) => (
                        <Box
                          key={certificate.id}
                          py={0.5}
                          px={1.5}
                          border={1}
                          borderRadius={3}
                          borderColor={theme.palette.grey[500]}
                        >
                          <Typography variant="subtitle2">
                            {certificate.title}
                          </Typography>
                        </Box>
                      ))}
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
          <Grid container width="100%" mt={2}>
            <Paper sx={paperStylesSx}>
              <Tabs
                value={activeTab}
                onChange={handleChange}
                TabIndicatorProps={noIndicator}
                sx={tabStylesSx}
                variant="scrollable"
                scrollButtons={false}
              >
                {tabs.map(({ label }, index) => (
                  <Tab key={String(index)} label={label} />
                ))}
              </Tabs>
              <Divider />
              <Box>
                {CurrentTab?.component && (
                  <CurrentTab.component
                    id={CurrentTab?.id}
                    uuid={CurrentTab?.uuid}
                    candidate={candidate?.data}
                  />
                )}
              </Box>
            </Paper>
          </Grid>
        </>
      )}
    </PageLayout>
  );
}
